import { Component, Input, OnInit } from '@angular/core';
import { IAccountArrangementItem } from '../interfaces/IAccountArrangementItem';
import { PRODUCT_KIND } from '../enums/payment-card-types';
import { PaymentCard } from '@backbase/ui-ang';

const DEFAULT_CARD_IMAGE = 'card-image-front.png';
const DEFAULT_ADDITIONAL_CARD_IMAGE = 'card-image-back.png';

@Component({
  selector: 'bn-account-detail-base',
  templateUrl: './account-detail-base.component.html',
})
export class BnAccountDetailBaseComponent implements OnInit {
  @Input() product!: IAccountArrangementItem;
  @Input() hostRef: any;
  @Input() title1 = '';
  @Input() title2 = '';
  @Input() title3 = '';
  @Input() title4 = '';
  @Input() productNumberFormat = {
    length: 16,
    maskRange: [0, 12],
    segments: 4,
  };
  cardError = false;
  card: PaymentCard | undefined;
  additionalCards: PaymentCard[] = [];

  cardDateUnavailable = false;

  cardImageUrl = this.buildAssetUrl(DEFAULT_CARD_IMAGE);
  additionalCardImageUrl = this.buildAssetUrl(DEFAULT_ADDITIONAL_CARD_IMAGE);

  async ngOnInit(): Promise<void> {
    const {
      mainCardExpirationDate,
      mainCardNumber,
      mainCardName,
      cardType,
      cardTypeCode,
      additionalCardNumbers,
      additionalCardNames,
      additionalCardExpirationDates,
    } = this.product.additions;

    if (this.product.product?.productKind?.externalKindId) {
      this.card = this.getPaymentCard(
        mainCardExpirationDate,
        mainCardNumber,
        mainCardName,
        cardType,
        this.product.product?.productKind?.externalKindId,
        cardTypeCode,
      );
    }

    if (this.card?.cardName) {
      this.cardImageUrl = await this.resolveCardImageUrl(this.card.cardName, DEFAULT_CARD_IMAGE);
    }

    if (additionalCardNumbers && +additionalCardNumbers !== -1) {
      const additionalCardNumbersArray = additionalCardNumbers.split(',') || [];
      const additionalCardNamesArray = additionalCardNames.split(',') || [];
      const additionalCardExpirationDatesArray = additionalCardExpirationDates
        .split(',')
        .map((date) => date.split('/'));

      this.additionalCards = this.getAdditionalCards(
        additionalCardNumbersArray,
        additionalCardNamesArray,
        additionalCardExpirationDatesArray,
      );
    }
  }

  /**
   * Get payment card from account arrangement
   * @param {string} mainCardExpirationDate
   * @param {string} mainCardNumber
   * @param {string} mainCardName
   * @param {string} cardType
   * @param {string} productKind
   * @param {string} cardTypeCode
   */
  getPaymentCard(
    mainCardExpirationDate: string,
    mainCardNumber: string,
    mainCardName: string,
    cardType: string,
    productKind: string,
    cardTypeCode: string,
  ): PaymentCard | undefined {
    if (
      !productKind ||
      !mainCardNumber ||
      mainCardNumber === '-1' ||
      !mainCardExpirationDate || // Verificamos si mainCardExpirationDate está indefinido o es '-1'
      (productKind === PRODUCT_KIND.KIND4 && cardTypeCode === 'M')
    ) {
      this.cardError = true;
      return;
    }

    let expirationDate: Date | undefined = undefined; // Inicializamos la fecha de expiración como undefined

    if (mainCardExpirationDate !== '-1') {
      const [dayOfDate, monthOfDate, yearOfDate] = mainCardExpirationDate.split('-');

      if (monthOfDate && yearOfDate) {
        expirationDate = new Date(`${monthOfDate}/${dayOfDate}/${yearOfDate}`);
      }
    }

    return {
      number: mainCardNumber,
      vendor: '',
      name: mainCardName === 'NOT_AVAILABLE' ? '' : mainCardName || '',
      expirationDate,
      cardName: cardType || ''
    };
  }

  /**
   * Get additional card details
   * @param {string[]} cardNumbers
   * @param {string[]} cardNames
   * @param {string[][]} cardExpirationDates
   */
  getAdditionalCards(cardNumbers: string[], cardNames: string[], cardExpirationDates: string[][]): PaymentCard[] {
    return cardNumbers.reduce((cards: PaymentCard[], item: string, index: number) => {
      cards.push({
        number: item,
        vendor: '',
        name: cardNames?.[index] === 'NOT_AVAILABLE' ? '' : cardNames?.[index] || '',
        expirationDate: new Date(`${cardExpirationDates[index][0]}/01/${cardExpirationDates[index][1]}`),
        cardName: '',
      });
      return cards;
    }, []);
  }

  getImageProduct() {
    let image = '';
    switch (this.product.product?.productKind?.externalKindId) {
      case "kind1":
        image = "bb-payment-image-current-account";
        break;

      case "kind2":
        image = "bb-payment-image-saving-account";
        break;

      case "kind3":
        image = "bb-payment-image-saving-account";
        break;

      case "kind4":
        image = "bb-payment-image-multicredit";
        break;

      case "kind5":
        image = "bb-payment-image-loans";
        break;

      case "kind6":
        image = "bb-payment-image-certificate";
        break;
    }
    return image;
  }

  /**
   * Resolve the asset image URL for a given card product name (e.g. "Visa copa"),
   * falling back to a default image if no matching asset exists.
   * @param {string} cardName
   * @param {string} fallbackFileName
   */
  private async resolveCardImageUrl(cardName: string, fallbackFileName: string): Promise<string> {
    const url = this.buildAssetUrl(`${this.normalizeCardName(cardName)}.png`);
    const fallbackUrl = this.buildAssetUrl(fallbackFileName);

    try {
      const response = await fetch(url);
      return response.ok ? url : fallbackUrl;
    } catch {
      return fallbackUrl;
    }
  }

  /**
   * Normalize a card product name into an asset file name slug:
   * lowercases and replaces spaces with underscores (matches the
   * cards feature's asset naming convention, accents included).
   * @param {string} cardName
   */
  private normalizeCardName(cardName: string): string {
    return (cardName || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_');
  }

  private buildAssetUrl(fileName: string): string {
    const base = document.location.origin.includes('https')
      ? `${document.location.origin}/es-DO`
      : document.location.origin;
    return `${base}/assets/images/${fileName}`;
  }
}
